/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Table, notification, Tabs } from "antd";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ReactSVG } from "react-svg";
import CustomModal from "../../../components/modal";
import { useContextState } from "../../../context/ContextProvider";
import { addToNestedHeader } from "../../../features/nested_header/nested_header";
import {
  downloadModuleBackup,
  downloadTotalBackup,
  getFolderModules,
  getQboFolderModules,
  getModuleAttachments,
  getQboModuleAttachments,
  moduleFileDownload,
} from "../../../utils/organization";
import { organisationPage } from "../constants";
import { financialColumnValues } from "./constants";
import { useLocation } from "react-router-dom";
import "./folderContents.scss";

const FolderContents = ({ orgId, date, onModuleEnter, onBack, type = "xero" }) => {
  const { isLoading, getIdTokenClaims } = useAuth0();
  const [contents, setContents] = useState([]);
  const [financialContents, setFinancialContents] = useState([]);
  const [storageBreakdown, setStorageBreakdown] = useState(null);
  const [orgPlan, setOrgPlan] = useState({ files: 0, history_and_notes: 0, payroll: 0 });
  const [tableLoading, setTableLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const { orgFolderContentsAccountsPage, setOrgFolderContentsAccountsPage } = useContextState();
  const [modalOpen, setModalOpen] = useState({
    value: false,
    onConfirm: null,
  });

  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const { setBreadCrumb, breadCrumb, setNestedHeader } = useContextState();
  const dispatch = useDispatch();

  // Handle responsive screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      (async () => {
        try {
          const token = await getIdTokenClaims();

          const fetchApi = type === "qbo" ? getQboFolderModules : getFolderModules;

          fetchApi(token.__raw, orgId, date)
            .then((res) => {
              const rawFiles = res.data?.files ?? res.data?.modules ?? [];
              // QBO uses PascalCase module names (e.g. BalanceSheet) while Xero uses snake_case
              // (e.g. balance_sheet). Normalize before comparing so financial statements are
              // classified consistently across providers.
              const normalizeModuleKey = (value) =>
                String(value || "")
                  .toLowerCase()
                  .replace(/[^a-z0-9]/g, ""); // remove underscores/spaces/etc

              const financialKeySet = new Set(
                financialColumnValues.map((v) => normalizeModuleKey(v))
              );

              const isFinancial = (item) =>
                financialKeySet.has(normalizeModuleKey(item?.file));

              const nonFinancialContents = rawFiles.filter((item) => !isFinancial(item));
              const financialContents = rawFiles.filter((item) => isFinancial(item));
              if (res.data?.filesapi && res.data.filesapi) {
                nonFinancialContents.push(res.data.filesapi);
              }
              const orgData = res.data?.plan ?? res.data?.xero ?? res.data?.qbo ?? {};
              const filesSub = Number(orgData.files ?? res.data?.folder_size_breakdown?.has_files ?? 0);
              const historySub = Number(orgData.history_and_notes ?? res.data?.folder_size_breakdown?.has_history ?? 0);
              setOrgPlan({
                files: filesSub,
                history_and_notes: historySub,
                payroll: Number(orgData.payroll ?? 0),
              });
              if (res.data?.folder_size_breakdown) {
                setStorageBreakdown(res.data.folder_size_breakdown);
              }
              setContents(nonFinancialContents);
              setFinancialContents(financialContents);
            })
            .catch((err) => {
              console.log("err", err);
            })
            .finally(() => {
              setTableLoading(false);
            });
        } catch (e) {}
      })();
    }
  }, [isLoading, orgId, date, type]);

  const downloadFile = async (link, buttonId) => {
    const onConfirmDownload = async () => {
      try {
        setIsButtonLoading(true);

        const token = await getIdTokenClaims();

        moduleFileDownload(token.__raw, link)
          .then((res) => {
            notification.success({
              message: "You will receive an email once your download is complete with a link to download the files",
              duration: 5,
            });
          })
          .catch((err) => {
            console.error(err);
          })
          .finally(() => {
            onCloseModal();
          });
      } catch (e) {
        onCloseModal();
      }
    };
    setModalOpen({
      value: true,
      onConfirm: onConfirmDownload,
    });
  };

  const resolveDownloadLink = (val, kind) => {
    if (!val) return null;
    if (kind === "csv") {
      return val.csv_link ?? val.csvLink ?? val.csv_down_link ?? val.csv_down ?? null;
    }
    if (kind === "excel") {
      return val.xls_link ?? val.excel_link ?? val.excelLink ?? val.xlsx_link ?? null;
    }
    if (kind === "json") {
      return val.json_link ?? val.jsonLink ?? val.json_down_link ?? val.json_down ?? null;
    }
    return null;
  };

  const resolveOrgItemId = (val) => {
    return val?.xero_id ?? val?.qbo_id ?? val?.id ?? null;
  };

  const handleAttachmentsClick = async (id, moduleName, date) => {
    const onConfirmDownload = async () => {
      try {
        setIsButtonLoading(true);

        const token = await getIdTokenClaims();

        const fetchApi = type === "qbo" ? getQboModuleAttachments : getModuleAttachments;

        fetchApi(token.__raw, id, date, moduleName)
          .then((res) => {
            console.log(res.data);
          })
          .catch((err) => {
            console.error(err);
          });
      } catch (e) {
        console.log(e);
      } finally {
        onCloseModal();
      }
    };

    setModalOpen({
      value: true,
      onConfirm: onConfirmDownload,
    });
  };

  const downloadBackup = async (file, buttonId) => {
    const onConfirmDownload = async () => {
      try {
        setIsButtonLoading(true);

        const token = await getIdTokenClaims();
        downloadModuleBackup(
          token.__raw,
          resolveOrgItemId(file),
          file.rec_date,
          file.file
        )
          .then((res) => {
            notification.success({
              message: "You will receive an email once your download is complete with a link to download the files",
              duration: 5,
            });
          })
          .catch((err) => {
            console.error(err);
          })
          .finally(() => {
            onCloseModal();
          });
      } catch (e) {
        onCloseModal();
      }
    };

    setModalOpen({
      value: true,
      onConfirm: onConfirmDownload,
    });
  };

  const downloadWholeBackup = async () => {
    const onConfirmDownload = async () => {
      try {
        setIsButtonLoading(true);

        const token = await getIdTokenClaims();

        downloadTotalBackup(token.__raw, orgId, date)
          .then((res) => {
            notification.success({
              message: "You will receive an email once your download is complete with a link to download the files",
              duration: 5,
            });
          })
          .catch((err) => {
            console.error(err);
          })
          .finally(() => {
            onCloseModal();
          });
      } catch (e) {
        onCloseModal();
      }
    };

    setModalOpen({
      value: true,
      onConfirm: onConfirmDownload,
    });
  };

  const handleTableChange = (pagination) => {
    setOrgFolderContentsAccountsPage(pagination.current);
  };

  const financialColumns = [
    {
      title: "Financial statements",
      dataIndex: "finance",
      key: "finance",
      width: isMobile ? "15%" : "30%",
      ellipsis: true,
      fixed: "left",
      sorter: (a, b) => (a.file || "").localeCompare(b.file || ""),
      defaultSortOrder: "ascend",
      render: (key, { id, file, size }) => {
        return (
          <div className="cursor-default flex justify-between items-center flex-grow">
            <div
              onClick={() => {}}
              className={`flex items-center gap-3 py-1.5`}
            >
              <ReactSVG src={"/assets/icons/folder.svg"} />
              {file} {size ? <span>({size} MB)</span> : null}
            </div>
          </div>
        );
      },
    },
    {
      title: "Records",
      dataIndex: "records",
      key: "records",
      width: "15%",
      ellipsis: true,
      render: (_, { count }) => {
        return <div className="flex gap-2">{count}</div>;
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: isMobile ? "70%" : "55%",
      ellipsis: true,
      render: (_, val) => (
        <div className="flex gap-2">
          {(() => {
            const buttons = [];
            if (val.count === 0) return buttons;

            if (val.csv_id) {
              const link = resolveDownloadLink(val, "csv");
              buttons.push(
                <Button
                  key={`csv-${val.csv_id}`}
                  className="px-3 font-medium py-1 mr-2 rounded-lg border border-gray-300"
                  onClick={() =>
                    link
                      ? downloadFile(link, `csv-${val.csv_id}`)
                      : notification.error({
                          message: "Download link missing for CSV",
                          duration: 5,
                        })
                  }
                >
                  CSV
                </Button>
              );
            }
            if (val.excel_id) {
              const link = resolveDownloadLink(val, "excel");
              buttons.push(
                <Button
                  key={`excel-${val.excel_id}`}
                  className="px-3 font-medium py-1 mr-2 rounded-lg border border-gray-300"
                  onClick={() =>
                    link
                      ? downloadFile(link, `excel-${val.excel_id}`)
                      : notification.error({
                          message: "Download link missing for Excel",
                          duration: 5,
                        })
                  }
                >
                  Excel
                </Button>
              );
            }
            if (val.json_id) {
              const link = resolveDownloadLink(val, "json");
              buttons.push(
                <Button
                  key={`json-${val.json_id}`}
                  className="px-3 font-medium py-1 mr-2 rounded-lg border border-gray-300"
                  onClick={() =>
                    link
                      ? downloadFile(link, `json-${val.json_id}`)
                      : notification.error({
                          message: "Download link missing for JSON",
                          duration: 5,
                        })
                  }
                >
                  JSON
                </Button>
              );
            }
            if (val.attach_count && val.attach_count > 0) {
              buttons.push(
                <div
                  key={`attachments-${val.attach_count}`}
                  className="px-3 font-medium py-1 mr-2 rounded-lg border border-gray-300"
                  onClick={() =>
                    handleAttachmentsClick(resolveOrgItemId(val), val.file, val.rec_date)
                  }
                >
                  Attachments ({val.attach_count})
                </div>
              );
            }

            buttons.push(
              <Button
                key={`download-backup-${val.id}`}
                className="px-3 font-medium py-1 mr-2 rounded-lg border border-gray-300"
                onClick={() => downloadBackup(val, `download-backup-${val.id}`)}
              >
                Download {val.file.replace(/_/g, " ")}
              </Button>
            );

            return buttons;
          })()}
        </div>
      ),
    },
  ];

  const columns = [
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      width: isMobile ? "15%" : "30%",
      ellipsis: true,
      fixed: "left",
      sorter: (a, b) => (a.file || "").localeCompare(b.file || ""),
      defaultSortOrder: "ascend",
      render: (key, { id, file, size }) => {
        return (
          <div className="cursor-pointer flex justify-between items-center flex-grow">
            <div
              onClick={() => {
                onModuleEnter(id, file);
                const nextPage = {
                  header: file,
                  page: organisationPage.ORGANISATION_FOLDER_CONTENTS_DETAILS,
                };

                dispatch(addToNestedHeader(nextPage));
                setNestedHeader(nextPage);
                setBreadCrumb((prevState) => [
                  ...prevState,
                  { label: file, url: window.location.href },
                ]);
              }}
              className={`flex items-center gap-3 py-1.5 cursor-pointer`}
            >
              <ReactSVG src={"/assets/icons/folder.svg"} />
              {file} {size ? <span>({size} MB)</span> : null}
            </div>
          </div>
        );
      },
    },
    {
      title: "Records",
      dataIndex: "records",
      key: "records",
      width: "15%",
      ellipsis: true,
      render: (_, { count }) => {
        return <div className="flex gap-2">{count}</div>;
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: isMobile ? "70%" : "55%",
      ellipsis: true,
      render: (_, val) => (
        <div className="flex gap-2">
          {(() => {
            const buttons = [];
            if (val.count === 0) return buttons;

            if (val.csv_id) {
              const link = resolveDownloadLink(val, "csv");
              buttons.push(
                <Button
                  key={`csv-${val.csv_id}`}
                  className="px-3 font-medium py-1 mr-2 rounded-lg border border-gray-300"
                  onClick={() =>
                    link
                      ? downloadFile(link, `csv-${val.csv_id}`)
                      : notification.error({
                          message: "Download link missing for CSV",
                          duration: 5,
                        })
                  }
                >
                  CSV
                </Button>
              );
            }
            if (val.excel_id) {
              const link = resolveDownloadLink(val, "excel");
              buttons.push(
                <Button
                  key={`excel-${val.excel_id}`}
                  className="px-3 font-medium py-1 mr-2 rounded-lg border border-gray-300"
                  onClick={() =>
                    link
                      ? downloadFile(link, `excel-${val.excel_id}`)
                      : notification.error({
                          message: "Download link missing for Excel",
                          duration: 5,
                        })
                  }
                >
                  Excel
                </Button>
              );
            }
            if (val.json_id) {
              const link = resolveDownloadLink(val, "json");
              buttons.push(
                <Button
                  key={`json-${val.json_id}`}
                  className="px-3 font-medium py-1 mr-2 rounded-lg border border-gray-300"
                  onClick={() =>
                    link
                      ? downloadFile(link, `json-${val.json_id}`)
                      : notification.error({
                          message: "Download link missing for JSON",
                          duration: 5,
                        })
                  }
                >
                  JSON
                </Button>
              );
            }
            if (val.attach_count && val.attach_count > 0) {
              buttons.push(
                <div
                  key={`attachments-${val.attach_count}`}
                  className="px-3 font-medium py-1 mr-2 rounded-lg border border-gray-300"
                  onClick={() =>
                    handleAttachmentsClick(resolveOrgItemId(val), val.file, val.rec_date)
                  }
                >
                  Attachments ({val.attach_count})
                </div>
              );
            }

            buttons.push(
              <Button
                key={`download-backup-${val.id}`}
                className="px-3 font-medium py-1 mr-2 rounded-lg border border-gray-300"
                onClick={() => downloadBackup(val, `download-backup-${val.id}`)}
              >
                Download{" "}
                {val.file ? val.file.replace(/_/g, " ") : "No file exist"}
              </Button>
            );

            return buttons;
          })()}
        </div>
      ),
    },
  ];

  const onCloseModal = () => {
    setModalOpen({
      value: false,
      onConfirm: null,
    });
    setIsButtonLoading(false);
  };

  const location = useLocation();

  useEffect(() => {
    return () => {
      if (location.pathname !== "/organizations") {
        setOrgFolderContentsAccountsPage(null);
      }
    };
  }, [location.pathname]);

  return (
    <div>
      <div className={"flex items-center justify-between my-2"}>
        <div className="flex items-center gap-2">
          <Button onClick={() => {
			setOrgFolderContentsAccountsPage(null)
			onBack()
		  }} className="flex items-center" type="default">
            <ReactSVG src="/assets/icons/arrow-left.svg" />
            <span className="ml-2">Back</span>
          </Button>

          <div className="flex items-center">
            {breadCrumb.map((item, index) => {
              const isLastItem = index === breadCrumb.length - 1;
              return (
                <div key={index} className="flex items-center">
                  <p className={isLastItem ? "font-semibold" : ""}>
                    {item.label}
                  </p>
                  {!isLastItem && <span className="mx-2">&gt;</span>}
                </div>
              );
            })}
          </div>
        </div>

        {(contents.length > 0 || financialContents.length > 0) && (
          <Button type="primary" onClick={downloadWholeBackup}>
            Download as ZIP
          </Button>
        )}
      </div>

      <CustomModal
        modalOpen={modalOpen.value}
        onClose={onCloseModal}
        title={
          <div className="text-white text-lg font-bold bg-black px-4 py-2">
            Terms of Service
          </div>
        }
        footer={[
          <Button
            key="disagree"
            onClick={onCloseModal}
            className="bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-md px-4 py-2"
          >
            I Disagree
          </Button>,
          <Button
            key="agree"
            type="primary"
            onClick={modalOpen.onConfirm}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-md px-4 py-2"
            loading={isButtonLoading}
          >
            I Agree
          </Button>,
        ]}
      >
        <div className="text-gray-700 text-sm leading-6 max-h-72 overflow-y-auto">
          <div className="px-6 py-4">
						<b>Secure Download Request</b>

						<p>
							Once you confirm, an email will be sent to all users linked to this organization.

							<br/><br/>

							<strong>30-Minute Cancel Window:</strong><br/>
							Every org member will receive an email with a Cancel link. If anyone cancels within 30 minutes, the download will be stopped.

							<br/><br/>

							<strong>If No One Cancels:</strong><br/>

							After 30 minutes, you'll receive another email with a secure download link and password. You'll need to enter the password to open the file.

							<br/><br/>

							<strong>Important Notes:</strong><br/>

							<ul>
								<li>Any user in your organization can cancel within 30 minutes.</li>
								<li>Download link expires after 24 hours.</li>
								<li>After downloading, you'll get a final email with the extraction password.</li>
							</ul>
						</p>
          </div>
        </div>
      </CustomModal>

      {storageBreakdown && (() => {
        const hasFilesSub = !!(orgPlan.files || storageBreakdown.has_files);
        const hasHistorySub = !!(orgPlan.history_and_notes || storageBreakdown.has_history);
        const effectiveTotalMb = storageBreakdown.accounting_mb + (hasFilesSub ? storageBreakdown.files_mb : 0) + (hasHistorySub ? storageBreakdown.history_mb : 0);

        return (
          <div className="mb-4 p-3.5 bg-gradient-to-r from-blue-50/70 via-indigo-50/50 to-slate-50 border border-blue-100/80 rounded-xl flex items-center justify-between flex-wrap gap-3 shadow-xs">
            <div className="flex items-center gap-2.5">
              <span className="text-xs uppercase font-bold tracking-wider text-gray-500">Backup Storage</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                {effectiveTotalMb.toFixed(2)} MB Total
              </span>
            </div>
            <div className="flex items-center gap-2.5 flex-wrap text-xs">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200/80 rounded-lg text-gray-700 shadow-2xs">
                <span>📊</span>
                <span className="text-gray-500">Accounting Data:</span>
                <span className="font-semibold text-gray-900">{storageBreakdown.accounting_mb.toFixed(2)} MB</span>
              </span>
              {hasFilesSub && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200/80 rounded-lg text-gray-700 shadow-2xs">
                  <span>📁</span>
                  <span className="text-gray-500">Files & Folders:</span>
                  <span className="font-semibold text-gray-900">{storageBreakdown.files_mb.toFixed(2)} MB</span>
                </span>
              )}
              {hasHistorySub && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200/80 rounded-lg text-gray-700 shadow-2xs">
                  <span>📜</span>
                  <span className="text-gray-500">History & Notes:</span>
                  <span className="font-semibold text-gray-900">{storageBreakdown.history_mb.toFixed(2)} MB</span>
                </span>
              )}
            </div>
          </div>
        );
      })()}

	  <Tabs
		defaultActiveKey="module"
		items={[
		  {
			key: "module",
			label: "Accounts",
			children: (
			  <Table
				sticky={true}
				dataSource={contents}
				columns={columns}
				loading={tableLoading}
				onChange={handleTableChange}
				pagination={{
				  current: orgFolderContentsAccountsPage ?? 1,
				  pageSize: 10,
				  total: contents.length,
				}}
				scroll={{ x: 1000 }}
			  />
			),
		  },
		  {
			key: "financial",
			label: "Financial Statements",
			children: (
			  <Table
				sticky={true}
				dataSource={financialContents}
				columns={financialColumns}
				loading={tableLoading}
				pagination={false}
				scroll={{ x: 1000 }}
			  />
			),
		  },
		]}
	  />
    </div>
  );
};

export default FolderContents;
